import { GetApiTokenListParams, useGetApiTokenList, UserType } from "@bvhi/backend-api";
import { Alert, Card, Stack, Typography } from "@bvhi/ui-base";
import { ErrorIcon } from "@bvhi/ui-icons";
import { keepPreviousData } from "@tanstack/react-query";
import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BVHINavigationCreateEntityButton } from "../../../../components/bvhi-navigation-create-entity-button";
import { NavigationBar } from "../../../../components/navigation-bar";
import { useAuthContext } from "../../../../context/use-auth-context";
import { useBVHIDataGridFilter } from "../../../../hooks/use-bvhi-datagrid-Filter";
import { useErrorObjectMapper } from "../../../../hooks/use-error-object-mapper";
import { Route } from "../../../../routes/_main/app/api-token.index";
import { ApiTokenDataGrid } from "./components/datagrid/api-token-data-grid";

export const ApiTokenListView = () => {
  const { t } = useTranslation(["users", "api-token"]);
  const { currentUser } = useAuthContext();
  const searchParams = Route.useSearch();

  const { getErrorMessagesFromErrorObject } = useErrorObjectMapper();
  const { handleDataGridChange, requestParams } = useBVHIDataGridFilter<GetApiTokenListParams>({}, Route.fullPath, undefined, searchParams);

  const { error } = useGetApiTokenList(currentUser?.organisation?.id ?? "", requestParams, {
    query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
  });

  const errorMessages = useMemo(() => {
    if (!error?.errors) return [];
    const errItems = getErrorMessagesFromErrorObject(error);
    return errItems.map((errItem) => (
      <Alert key={errItem} icon={<ErrorIcon />} severity="error">
        {errItem}
      </Alert>
    ));
  }, [error, getErrorMessagesFromErrorObject]);

  return (
    <>
      <NavigationBar
        showBreadcrumbs={true}
        actions={[
          <BVHINavigationCreateEntityButton
            key="0"
            entity={t("api-token:api.token.entity")}
            navigateTarget={{ to: "/app/api-token/create" }}
          />,
        ]}
      />

      <Stack pt={2} spacing={2}>
        {errorMessages.map((errorComponent) => errorComponent)}
        <Suspense fallback={<Card sx={{ height: 300, minHeight: 300 }} />}>
          <ApiTokenDataGrid
            requestParams={requestParams}
            updateCallback={handleDataGridChange}
            subHeader={
              <Stack spacing={2}>
                <Typography>
                  {t(
                    currentUser?.type === UserType.ERP_SUPPLIER
                      ? "api-token:api.token.datagrid.sub.title.erp.supplier.1"
                      : "api-token:api.token.datagrid.sub.title.audiologist.1"
                  )}
                </Typography>
                <Typography>
                  {t(
                    currentUser?.type === UserType.ERP_SUPPLIER
                      ? "api-token:api.token.datagrid.sub.title.erp.supplier.2"
                      : "api-token:api.token.datagrid.sub.title.audiologist.2"
                  )}
                </Typography>
                <Typography>
                  {t("api-token:api.token.datagrid.sub.title.3.1")}&nbsp;
                  <a href="https://bvhi.org/hidatasync" rel="noreferrer" target="_blank">
                    {t("api-token:api.token.datagrid.sub.title.3.2")}
                  </a>
                  &nbsp;
                  {t("api-token:api.token.datagrid.sub.title.3.3")}
                </Typography>
              </Stack>
            }
          />
        </Suspense>
      </Stack>
    </>
  );
};
